import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

/**
 * 邮箱密码重置表单
 * 与登录/注册页面保持一致的简洁风格
 *
 * 功能：
 * - 输入邮箱地址
 * - 发送密码重置邮件
 * - 显示友好的错误提示
 * - 多语言支持
 */
export default function ForgotPasswordEmailForm() {
  const { t, i18n } = useTranslation("login");
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // 验证邮箱格式
    if (!email || !/\S+@\S+\.\S+/.test(email)) {
      toast.error(t("forgotPassword.invalidEmail"));
      return;
    }

    setIsLoading(true);

    try {
      const csrfToken =
        document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')
          ?.content || "";

      const response = await fetch("/users/password", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-CSRF-Token": csrfToken,
          "Accept-Language": i18n.language,
        },
        body: JSON.stringify({
          user: { email },
          locale: i18n.language,
        }),
      });

      if (response.ok) {
        window.location.href = "/forgot-password?sent=true";
      } else {
        toast.error(t("forgotPassword.sendFailed"));
      }
    } catch {
      toast.error(t("forgotPassword.networkError"));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="space-y-4"
      aria-label={t("forgotPassword.title")}
      aria-busy={isLoading}
    >
      {/* 邮箱输入 */}
      <div>
        <Input
          id="email"
          type="email"
          placeholder={t("forgotPassword.emailPlaceholder")}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          autoFocus
          disabled={isLoading}
          aria-required="true"
          aria-label={t("forgotPassword.emailLabel")}
          className="bg-background dark:bg-background"
        />
      </div>

      {/* 提交按钮 */}
      <Button
        type="submit"
        className="w-full"
        disabled={isLoading}
        aria-busy={isLoading}
      >
        {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
        {isLoading
          ? t("forgotPassword.submitting")
          : t("forgotPassword.submit")}
      </Button>
    </form>
  );
}
